/**
 * AssignProvidersDialog
 * ─────────────────────
 * Feb 2026 — Phase 3c.1 of B2 IntakeWizard extraction.
 *
 * Pure mechanical extraction of the inline dialog at App.js lines
 * ~70455–71062.  ~600 LOC of JSX moved into a standalone component
 * with zero behavior change.
 *
 * State ownership stays in App.js for Phase 3c.1 — the parent passes
 * all state + setters as props.  Phase 3c.2 will mirror this state
 * into IntakeWizardContext so V2 can mount the dialog natively and
 * stop bouncing.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { LinkProviderDiagnosesDialog } from './LinkProviderDiagnosesDialog';
import { isSuperAdmin } from '../../utils/helpers';
import { useArchivedProviders, ArchivedProvidersToggleRow, ArchivedProviderTag } from '../shared/ArchivedProvidersSupport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
});

export default function AssignProvidersDialog({
  // Dialog state
  open,
  onOpenChange,

  // Client context
  selectedClient,
  setSelectedClient,
  editingClient,

  // Reference data
  providers = [],
  users = [],
  serviceTypes = [],
  availableServices = [],

  // Enrollment state
  existingEnrollments = [],
  setExistingEnrollments,

  // Provider/service selection state
  selectedProvidersForAssignment = [],
  setSelectedProvidersForAssignment,
  providerServiceAssignments = {},
  setProviderServiceAssignments,

  // Reset helpers (parent owns these so it can reset everything when
  // cancelling).  Provided as a single callback for ergonomics.
  resetSelections,

  // Pending-enrollment plumbing (for new-client flow in IntakeWizard)
  showIntakeWizard,
  handleIntakeFieldChange,
  clientFormData,
  setClientFormData,
  setPendingServiceEnrollments,

  // Client-enrollment cache (legacy keeps an enrollment cache on App.js)
  setClientEnrollments,
  invalidateEnrollmentCache,
  enrollmentsCacheRef,
  // Mar 2026 — centralized reload helper.  Fetches with
  // include_inactive=true so Active + Pending + Past stay correct in
  // the chart header, and keeps both `clientEnrollments` and
  // `existingEnrollments` (Manage dialog) in sync after mutations.
  reloadClientEnrollments,

  // Refresh callbacks
  refreshNotes,
  refreshClients,
  refreshStats,

  // Custom confirm modal utility from parent
  confirmAction,
}) {
  // Mar 2026 — admin-only override for the discharge-form check.
  // Jun 2026 fix — the check was `role === 'admin' || is_superuser`, but
  // real admins in this system have role 'super admin' (see
  // utils/helpers.isSuperAdmin), so the override prompt NEVER showed and
  // admins just saw the raw "no signed discharge form" error.
  const { user: currentUser } = useAuth() || {};
  // Jun 2026 — queue of newly-assigned providers to offer diagnosis linking for.
  const [linkDxQueue, setLinkDxQueue] = React.useState([]);
  const isAdmin = isSuperAdmin(currentUser) || currentUser?.role === 'admin' || currentUser?.is_superuser;

  // Jun 2026 — optional archived-provider visibility (off by default,
  // resets whenever the dialog closes).
  const { includeArchived, toggleArchived, resetArchived, mergeProviders } = useArchivedProviders();
  React.useEffect(() => { if (!open) resetArchived(); }, [open, resetArchived]);
  const allProviders = mergeProviders(providers);

  // Local helper to close and reset state.  Parent-supplied
  // `resetSelections` is preferred (covers all reset state at once);
  // otherwise we fall back to inline setters.
  const handleClose = () => {
    if (typeof resetSelections === 'function') {
      resetSelections();
    } else {
      setSelectedProvidersForAssignment?.([]);
      setProviderServiceAssignments?.({});
      setExistingEnrollments?.([]);
    }
    onOpenChange?.(false);
  };

  // Jun 2026 — allow saving caseload-only changes (e.g. removing a
  // provider who has no enrollments).  Previously the save button only
  // enabled when NEW enrollments were being created, so a provider
  // removal (including removing all providers) could never be persisted.
  const hasNewEnrollments = selectedProvidersForAssignment.some(
    (id) => (providerServiceAssignments[id] || []).length > 0,
  );
  const caseloadChanged = (() => {
    if (!selectedClient || selectedClient.id === 'new') return false;
    const current = [...(selectedClient.assigned_providers || [])].sort();
    const next = [...selectedProvidersForAssignment].sort();
    return JSON.stringify(current) !== JSON.stringify(next);
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="assign-providers-dialog"
      >
        <DialogHeader>
          <DialogTitle>Assign Providers & Services to {selectedClient?.full_name}</DialogTitle>
          <DialogDescription>
            Select providers and the specific services they will provide to this client.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">

          {/* Existing enrollments */}
          {existingEnrollments.filter((e) => e.status === 'active' || e.status === 'assigned').length > 0 && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Current Service Enrollments:</h4>
              <div className="space-y-2">
                {existingEnrollments.filter((e) => e.status === 'active' || e.status === 'assigned').map((enrollment) => (
                  <div
                    key={enrollment.id}
                    className={`flex items-center justify-between text-sm bg-white p-2 rounded ${enrollment.disenrollment_pending ? 'border-2 border-amber-300' : ''}`}
                  >
                    <span>
                      <strong>{enrollment.provider_name}</strong> - {enrollment.service_name || enrollment.service_type}
                      <Badge className={`ml-2 ${enrollment.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {enrollment.status}
                      </Badge>
                      {enrollment.disenrollment_pending && (
                        <Badge className="ml-2 bg-amber-100 text-amber-800">
                          Pending Discharge
                        </Badge>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      {enrollment.start_date && (
                        <span className="text-gray-500 text-xs">
                          Started: {new Date(enrollment.start_date).toLocaleDateString()}
                        </span>
                      )}
                      {enrollment.disenrollment_pending && (
                        <>
                          {/* Feb 2026 — Approve Disenrollment.
                              Mar 2026 — Admin can now override the
                              discharge-form check when the system
                              can't auto-detect the discharge note
                              (legacy template, paper discharge, etc.). */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 border-emerald-300 h-7 px-2"
                            data-testid={`approve-disenrollment-${enrollment.id}`}
                            onClick={async () => {
                              const ok = await confirmAction?.(
                                `Approve and finalize the disenrollment for ${enrollment.provider_name} — ${enrollment.service_name || enrollment.service_type}?\n\nThis ends the enrollment.  A signed discharge form must already be on file.`,
                                { title: 'Approve disenrollment?', confirmLabel: 'Approve', destructive: false },
                              );
                              if (!ok) return;
                              try {
                                await axios.post(
                                  `${API}/service-enrollments/${enrollment.id}/complete-disenrollment`,
                                  {},
                                  authHeader(),
                                );
                                toast.success('Disenrollment approved and completed');
                                if (reloadClientEnrollments) {
                                  await reloadClientEnrollments(selectedClient.id);
                                } else {
                                  const refreshRes = await axios.get(
                                    `${API}/clients/${selectedClient.id}/service-enrollments?include_inactive=true`,
                                    authHeader(),
                                  );
                                  setExistingEnrollments?.(refreshRes.data);
                                }
                              } catch (error) {
                                const detail = error.response?.data?.detail || 'Failed to approve disenrollment';
                                const isDischargeError =
                                  error.response?.status === 400
                                  && /discharge/i.test(detail);
                                if (isDischargeError && isAdmin) {
                                  // Offer admin override path.  Prompt
                                  // for a reason — it's stamped on the
                                  // enrollment for audit.
                                  const reason = window.prompt(
                                    `${detail}\n\nAs an admin, you can override this check.\n\nWhy are you overriding? (required, stamped on the enrollment for audit)`,
                                    '',
                                  );
                                  if (!reason || !reason.trim()) {
                                    toast.error('Override cancelled — a reason is required.');
                                    return;
                                  }
                                  try {
                                    const params = new URLSearchParams({
                                      override_discharge_check: 'true',
                                      override_reason: reason.trim(),
                                    });
                                    await axios.post(
                                      `${API}/service-enrollments/${enrollment.id}/complete-disenrollment?${params.toString()}`,
                                      {},
                                      authHeader(),
                                    );
                                    toast.success('Disenrollment approved (discharge check overridden — audit entry recorded)');
                                    if (reloadClientEnrollments) {
                                      await reloadClientEnrollments(selectedClient.id);
                                    } else {
                                      const refreshRes = await axios.get(
                                        `${API}/clients/${selectedClient.id}/service-enrollments?include_inactive=true`,
                                        authHeader(),
                                      );
                                      setExistingEnrollments?.(refreshRes.data);
                                    }
                                  } catch (overrideErr) {
                                    toast.error(overrideErr.response?.data?.detail || 'Override failed');
                                  }
                                } else {
                                  toast.error(detail);
                                }
                              }
                            }}
                          >
                            Approve Disenrollment
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-7 px-2"
                            onClick={async () => {
                              const confirmed = window.confirm(
                                `Cancel the pending disenrollment for ${enrollment.provider_name} - ${enrollment.service_name || enrollment.service_type}?\n\n`
                                + 'This will remove the pending discharge requirement and keep the enrollment active.',
                              );
                              if (!confirmed) return;
                              try {
                                await axios.post(
                                  `${API}/service-enrollments/${enrollment.id}/cancel-disenrollment`,
                                  {},
                                  authHeader(),
                                );
                                toast.success('Disenrollment cancelled');
                                if (reloadClientEnrollments) {
                                  await reloadClientEnrollments(selectedClient.id);
                                } else {
                                  const refreshRes = await axios.get(
                                    `${API}/clients/${selectedClient.id}/service-enrollments?include_inactive=true`,
                                    authHeader(),
                                  );
                                  setExistingEnrollments?.(refreshRes.data);
                                }
                              } catch (error) {
                                console.error('Cancel disenrollment error:', error);
                                toast.error(error.response?.data?.detail || 'Failed to cancel disenrollment');
                              }
                            }}
                          >
                            Cancel Disenrollment
                          </Button>
                        </>
                      )}
                      {!enrollment.disenrollment_pending && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                          onClick={async () => {
                            try {
                              const activityRes = await axios.get(
                                `${API}/service-enrollments/${enrollment.id}/check-activity`,
                                authHeader(),
                              );
                              if (activityRes.data.has_clinical_activity) {
                                const confirmed = window.confirm(
                                  `This enrollment has ${activityRes.data.notes_count} note(s) and ${activityRes.data.appointments_count} completed appointment(s).\n\n`
                                  + `A discharge form will be required from ${enrollment.provider_name} before the disenrollment can be completed.\n\n`
                                  + 'Continue?',
                                );
                                if (!confirmed) return;
                              }
                              const reasonsRes = await axios.get(
                                `${API}/service-enrollments/disenrollment-reasons`,
                                authHeader(),
                              );
                              const reasons = reasonsRes.data;
                              const reasonOptions = reasons.map((r, i) => `${i + 1}. ${r.label}`).join('\n');
                              const reasonChoice = window.prompt(
                                `Select reason for disenrollment:\n\n${reasonOptions}\n\nEnter number (1-${reasons.length}):`,
                              );
                              if (!reasonChoice) return;
                              const reasonIndex = parseInt(reasonChoice, 10) - 1;
                              if (Number.isNaN(reasonIndex) || reasonIndex < 0 || reasonIndex >= reasons.length) {
                                toast.error('Invalid selection');
                                return;
                              }
                              const selectedReason = reasons[reasonIndex];
                              let reasonNotes = null;
                              if (selectedReason.id === 'other') {
                                reasonNotes = window.prompt('Please specify the reason:');
                                if (!reasonNotes) return;
                              }
                              const initRes = await axios.post(
                                `${API}/service-enrollments/${enrollment.id}/initiate-disenrollment?reason=${encodeURIComponent(selectedReason.id)}${reasonNotes ? `&reason_notes=${encodeURIComponent(reasonNotes)}` : ''}`,
                                {},
                                authHeader(),
                              );
                              if (initRes.data.status === 'completed') {
                                toast.success('Service enrollment ended successfully');
                                setClientEnrollments?.((prev) => prev.filter((e2) => e2.id !== enrollment.id));
                                setExistingEnrollments?.((prev) => prev.filter((e2) => e2.id !== enrollment.id));
                                if (initRes.data.should_prompt_caseload_removal) {
                                  const removeCaseload = window.confirm(
                                    `${enrollment.provider_name} no longer has any active service enrollments with this client.\n\n`
                                    + 'Remove client from their caseload? (They will no longer see or interact with this client unless re-enrolled)',
                                  );
                                  if (removeCaseload) {
                                    await axios.patch(
                                      `${API}/clients/${selectedClient.id}`,
                                      { assigned_providers: selectedClient.assigned_providers?.filter((p) => p !== enrollment.provider_id) || [] },
                                      authHeader(),
                                    );
                                  }
                                }
                                axios.get(`${API}/clients/${selectedClient.id}/service-enrollments?include_inactive=true`, authHeader())
                                  .then((refreshRes) => {
                                    if (reloadClientEnrollments) {
                                      reloadClientEnrollments(selectedClient.id);
                                    } else {
                                      setClientEnrollments?.(refreshRes.data);
                                      setExistingEnrollments?.(refreshRes.data);
                                    }
                                  })
                                  .catch(console.warn);
                              } else {
                                setExistingEnrollments?.((prev) => prev.map((e2) => (
                                  e2.id === enrollment.id
                                    ? { ...e2, status: 'pending_discharge', disenrollment_pending: true, disenrollment_reason: selectedReason.id }
                                    : e2
                                )));
                                setClientEnrollments?.((prev) => prev.map((e2) => (
                                  e2.id === enrollment.id
                                    ? { ...e2, status: 'pending_discharge', disenrollment_pending: true, disenrollment_reason: selectedReason.id }
                                    : e2
                                )));
                                toast.success(initRes.data.message);
                                if (reloadClientEnrollments) {
                                  reloadClientEnrollments(selectedClient.id);
                                } else {
                                  axios.get(`${API}/clients/${selectedClient.id}/service-enrollments?include_inactive=true`, authHeader())
                                    .then((refreshRes) => {
                                      setExistingEnrollments?.(refreshRes.data);
                                      setClientEnrollments?.(refreshRes.data);
                                    })
                                    .catch(console.warn);
                                }
                              }
                            } catch (error) {
                              console.error('Disenrollment error:', error);
                              toast.error(error.response?.data?.detail || 'Failed to process disenrollment');
                            }
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Provider and Service Selection */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-medium">Step 1: Select Provider</Label>
              {providers.length === 0 ? (
                <div className="text-center p-8 border rounded-lg bg-gray-50 mt-2">
                  <p className="text-gray-500">Loading providers...</p>
                  <p className="text-sm text-gray-400 mt-2">If this persists, no providers are available in the system.</p>
                </div>
              ) : (
                <Select
                  value=""
                  onValueChange={(providerId) => {
                    if (providerId && !selectedProvidersForAssignment.includes(providerId)) {
                      setSelectedProvidersForAssignment?.([...selectedProvidersForAssignment, providerId]);
                    }
                  }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choose a provider to add..." />
                  </SelectTrigger>
                  <SelectContent>
                    <ArchivedProvidersToggleRow includeArchived={includeArchived} onToggle={toggleArchived} />
                    {allProviders
                      .filter((p) => !selectedProvidersForAssignment.includes(p.id))
                      // Active first (accepting providers at top), archived last
                      .sort((a, b) =>
                        ((a.is_archived ? 1 : 0) - (b.is_archived ? 1 : 0)) ||
                        ((b.accepting_new_clients === true ? 1 : 0) -
                         (a.accepting_new_clients === true ? 1 : 0)))
                      .map((provider) => (
                        <SelectItem key={provider.id} value={provider.id} className={provider.is_archived ? 'text-gray-400' : undefined}>
                          <span className="flex items-center gap-2">
                            {provider.full_name} {provider.credentials ? `(${provider.credentials})` : ''}
                            {provider.is_archived && <ArchivedProviderTag />}
                            {!provider.is_archived && provider.accepting_new_clients === true && (
                              <span data-testid={`assign-providers-accepting-chip-${provider.id}`} className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-1.5 py-px">
                                Accepting
                              </span>
                            )}
                            {!provider.is_archived && provider.accepting_new_clients === false && (
                              <span data-testid={`assign-providers-not-accepting-chip-${provider.id}`} className="text-[10px] font-medium text-gray-500 bg-gray-100 border border-gray-300 rounded-full px-1.5 py-px">
                                Not accepting
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selectedProvidersForAssignment.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-medium">Step 2: Select Services for Each Provider</Label>
                {selectedProvidersForAssignment.map((providerId) => {
                  const provider = allProviders.find((p) => p.id === providerId);
                  if (!provider) return null;
                  const assignedServices = providerServiceAssignments[providerId] || [];
                  return (
                    <div
                      key={providerId}
                      className="border rounded-lg p-4 space-y-3 bg-gray-50"
                      data-testid={`assign-providers-step2-provider-${providerId}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium flex items-center gap-2">
                          {provider.full_name} {provider.credentials ? `(${provider.credentials})` : ''}
                          {provider.is_archived && <ArchivedProviderTag />}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`assign-providers-remove-provider-${providerId}`}
                          onClick={() => {
                            setSelectedProvidersForAssignment?.(selectedProvidersForAssignment.filter((id) => id !== providerId));
                            const newAssignments = { ...providerServiceAssignments };
                            delete newAssignments[providerId];
                            setProviderServiceAssignments?.(newAssignments);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Select services for this provider:</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {availableServices.map((service) => {
                            const isServiceSelected = assignedServices.includes(service.id);
                            const hasExistingEnrollment = existingEnrollments.some(
                              (e) => e.provider_id === providerId && e.service_type === service.id && e.status === 'active',
                            );
                            return (
                              <div key={service.id} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`service-${providerId}-${service.id}`}
                                  checked={isServiceSelected || hasExistingEnrollment}
                                  disabled={hasExistingEnrollment}
                                  onChange={(e) => {
                                    const newAssignments = { ...providerServiceAssignments };
                                    if (!newAssignments[providerId]) newAssignments[providerId] = [];
                                    if (e.target.checked) {
                                      newAssignments[providerId].push(service.id);
                                    } else {
                                      newAssignments[providerId] = newAssignments[providerId].filter((id) => id !== service.id);
                                    }
                                    setProviderServiceAssignments?.(newAssignments);
                                  }}
                                  className="h-4 w-4"
                                />
                                <label
                                  htmlFor={`service-${providerId}-${service.id}`}
                                  className={`text-sm cursor-pointer ${hasExistingEnrollment ? 'text-gray-400' : ''}`}
                                >
                                  {service.name}
                                  {hasExistingEnrollment && <span className="ml-1 text-green-600">(enrolled)</span>}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                        {assignedServices.length === 0 && !existingEnrollments.some((e) => e.provider_id === providerId && e.status === 'active') && (
                          <p className="text-sm text-amber-600 italic">Please select at least one service for this provider.</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            data-testid="assign-providers-cancel"
          >
            Cancel
          </Button>
          <Button
            data-testid="assign-providers-submit"
            onClick={async () => {
              // Track success across the outer try so a late (post-POST) throw
              // doesn't trigger a misleading "Failed to create enrollments" toast
              // when the backend already persisted the records.
              // (Kept as-is for behavioral parity with App.js.)
              try {
                // New-client branch: just save selections for later creation
                if (selectedClient?.id === 'new') {
                  if (showIntakeWizard) {
                    handleIntakeFieldChange?.('assigned_providers', selectedProvidersForAssignment);
                    handleIntakeFieldChange?.('pending_service_enrollments', { ...providerServiceAssignments });
                  } else if (typeof setClientFormData === 'function') {
                    setClientFormData({ ...clientFormData, assigned_providers: selectedProvidersForAssignment });
                  }
                  setPendingServiceEnrollments?.({ ...providerServiceAssignments });
                  toast.success('Provider and service assignments saved. Complete the form to create the client.');
                  handleClose();
                  return;
                }

                // Existing-client branch
                const enrollmentPromises = [];
                const optimisticEnrollments = [];
                for (const providerId of selectedProvidersForAssignment) {
                  const services = providerServiceAssignments[providerId] || [];
                  const providerObj = users.find((u) => u.id === providerId);
                  for (const serviceId of services) {
                    const existingEnrollment = existingEnrollments.find(
                      (e) => e.provider_id === providerId
                        && e.service_type === serviceId
                        && (e.status === 'active' || e.status === 'assigned'),
                    );
                    if (!existingEnrollment) {
                      optimisticEnrollments.push({
                        id: `temp-${Date.now()}-${providerId}-${serviceId}`,
                        provider_id: providerId,
                        provider_name: providerObj ? `${providerObj.first_name} ${providerObj.last_name}` : 'Unknown',
                        service_type: serviceId,
                        service_type_name: serviceTypes.find((s) => s.id === serviceId)?.name || serviceId,
                        status: 'active',
                        visit_frequency: 'weekly',
                        created_at: new Date().toISOString(),
                        _optimistic: true,
                      });
                      enrollmentPromises.push(
                        axios
                          .post(
                            `${API}/clients/${selectedClient.id}/service-enrollments`,
                            { provider_id: providerId, service_type: serviceId, visit_frequency: 'weekly' },
                            authHeader(),
                          )
                          .catch((err) => {
                            // Coerce `detail` to string first (Pydantic errors are arrays).
                            const detailStr = (() => {
                              const d = err.response?.data?.detail;
                              if (d == null) return '';
                              if (typeof d === 'string') return d;
                              try { return JSON.stringify(d); } catch { return String(d); }
                            })();
                            if ((err.response?.status === 400 || err.response?.status === 409)
                                && detailStr.includes('already enrolled')) {
                              return null;
                            }
                            throw err;
                          }),
                      );
                    }
                  }
                }

                if (optimisticEnrollments.length > 0) {
                  setClientEnrollments?.((prev) => [...prev, ...optimisticEnrollments]);
                }

                let successCount = 0;
                let errorCount = 0;
                if (enrollmentPromises.length > 0) {
                  const results = await Promise.allSettled(enrollmentPromises);
                  results.forEach((result) => {
                    if (result.status === 'fulfilled') {
                      if (result.value !== null) successCount += 1;
                    } else {
                      const err = result.reason;
                      const detailStr = (() => {
                        const d = err?.response?.data?.detail;
                        if (d == null) return '';
                        if (typeof d === 'string') return d;
                        try { return JSON.stringify(d); } catch { return String(d); }
                      })();
                      if (!((err?.response?.status === 400 || err?.response?.status === 409)
                            && detailStr.includes('already enrolled'))) {
                        errorCount += 1;
                        console.error('Enrollment creation error:', err);
                      }
                    }
                  });
                  if (successCount > 0) toast.success(`Created ${successCount} service enrollment(s)`);
                  else if (errorCount === 0) toast.info('All selected enrollments already exist');
                  else toast.warning(`Some enrollments failed (${errorCount} errors)`);
                } else {
                  toast.info('No new enrollments to create');
                }

                // Update assigned_providers (backward-compat).
                try {
                  await axios.put(
                    `${API}/clients/${selectedClient.id}`,
                    { assigned_providers: selectedProvidersForAssignment },
                    authHeader(),
                  );
                } catch (updateError) {
                  console.warn('Failed to update assigned_providers field:', updateError);
                }

                // Update form data if editing.
                if (editingClient) {
                  if (showIntakeWizard) {
                    handleIntakeFieldChange?.('assigned_providers', selectedProvidersForAssignment);
                  } else if (typeof setClientFormData === 'function') {
                    setClientFormData({ ...clientFormData, assigned_providers: selectedProvidersForAssignment });
                  }
                }

                // Refresh client lists / notes / stats.
                try {
                  refreshNotes?.();
                  refreshClients?.();
                  refreshStats?.();
                } catch (fetchError) {
                  console.warn('Failed to refresh data after enrollment:', fetchError);
                }

                // Refresh enrollment cache via the centralized helper
                // so both the chart header (`clientEnrollments`) and
                // the Manage dialog (`existingEnrollments`) stay in sync.
                if (selectedClient?.id) {
                  try {
                    invalidateEnrollmentCache?.(selectedClient.id);
                    if (reloadClientEnrollments) {
                      await reloadClientEnrollments(selectedClient.id);
                    } else {
                      const response = await axios.get(
                        `${API}/clients/${selectedClient.id}/service-enrollments?include_inactive=true`,
                        authHeader(),
                      );
                      if (enrollmentsCacheRef?.current) {
                        enrollmentsCacheRef.current[selectedClient.id] = response.data;
                      }
                      setClientEnrollments?.(response.data);
                    }
                  } catch (refreshError) {
                    console.warn('Failed to refresh enrollments:', refreshError);
                  }
                }

                // Re-fetch the client itself.
                try {
                  const clientResponse = await axios.get(
                    `${API}/clients/${selectedClient.id}`,
                    authHeader(),
                  );
                  setSelectedClient?.(clientResponse.data);
                } catch (clientError) {
                  console.warn('Failed to refresh client data:', clientError);
                }

                // Jun 2026 — offer to link the newly-assigned provider(s) to
                // the client's existing diagnoses (additive only; user picks).
                try {
                  const newProviderIds = [...new Set(optimisticEnrollments.map((e) => e.provider_id))];
                  if (successCount > 0 && newProviderIds.length) {
                    const dxRes = await axios.get(`${API}/clients/${selectedClient.id}/diagnoses`, authHeader());
                    const dxList = (Array.isArray(dxRes.data) ? dxRes.data : dxRes.data?.diagnoses || [])
                      .filter((d) => d.is_active !== false && (d.status || 'active') === 'active');
                    const queue = newProviderIds
                      .filter((pid) => dxList.some((d) => !(d.provider_ids || []).includes(pid)))
                      .map((pid) => {
                        const u = users.find((x) => x.id === pid);
                        return { id: pid, name: u ? `${u.first_name} ${u.last_name}` : 'Provider' };
                      });
                    if (queue.length) { setLinkDxQueue(queue); return; }
                  }
                } catch { /* non-fatal */ }

                handleClose();
              } catch (error) {
                console.error('Error creating service enrollments:', error);
                toast.error(error.response?.data?.detail || 'Failed to create service enrollments');
              }
            }}
            disabled={!hasNewEnrollments && !caseloadChanged}
          >
            {hasNewEnrollments ? 'Create Service Enrollments' : 'Save Changes'}
          </Button>
        </DialogFooter>
        <LinkProviderDiagnosesDialog
          open={linkDxQueue.length > 0}
          clientId={selectedClient?.id}
          provider={linkDxQueue[0]}
          onOpenChange={(o) => {
            if (!o) {
              setLinkDxQueue((q) => {
                const rest = q.slice(1);
                if (!rest.length) handleClose();
                return rest;
              });
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
